import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useWorkspaceChange from '../../../hooks/useWorkspaceChange';
import {
    Box,
    Button,
    Card,
    CardContent,
    Table,
    TableContainer,
    Typography,
    TablePagination,
    Pagination,
} from "@mui/material";
import { toast } from 'react-toastify';
import { useLanguage } from '../../../context/LanguageContext';
import { useUser } from '../../../context/UserContext';
import { PageTitle, PageContainer } from '../../../components/Common';
import DrawerVisualise from './components/DrawerVisualise';
import useCreateCustomersList from '../../../hooks/Clients/importList/useCreateCustomersList';
import useGetCustomersList from '../../../hooks/Clients/importList/useGetCustomersList';
import useGetDetailsCustomersList from '../../../hooks/Clients/importList/useGetDetailsCustomersList';
import useDeleteCustomersList from '../../../hooks/Clients/importList/useDeleteCustomersList';
import ImportForm from './components/ImportForm';
import ListTableHeader from './components/ListTableHeader';
import ListTableBody from './components/ListTableBody';
import ConfirmDialog from './components/ConfirmDialog';
import { ImportedList, LIST_TYPES, convertToDeleteFormat } from './types';

const INITIAL_PAGE = 0;
const DEFAULT_ROWS_PER_PAGE = 10;

const normalizeImportValue = (value: string): string => (
    value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
);

const readFirstNonEmptyLine = async (fileToRead: File): Promise<string> => {
    const text = await fileToRead.text();
    return text.split(/\r?\n/).find(line => line.trim().length > 0) || '';
};

const looksLikeHeaderLine = (line: string, selectedType: string): boolean => {
    const normalizedCells = line
        .split(/[;,\t|]/)
        .map(normalizeImportValue)
        .filter(Boolean);

    const firstCell = normalizedCells[0] || normalizeImportValue(line);
    const commonHeaders = new Set(['id', 'identifiant', 'identifier', 'valeur', 'value', 'liste', 'list']);
    const typeHeaders: Record<string, string[]> = {
        [LIST_TYPES.PERSONID]: ['personid', 'person', 'personne', 'clientid', 'customerid'],
        [LIST_TYPES.EMAIL]: ['email', 'mail', 'adressemail', 'emailaddress'],
        [LIST_TYPES.MOBILE]: ['telephone', 'telephoneportable', 'telephonemobile', 'mobile', 'mobilephone', 'cellphone', 'phone'],
        [LIST_TYPES.CARD]: ['carte', 'cartefidelite', 'card', 'cardnumber', 'numerocarte'],
        [LIST_TYPES.WALLET]: ['wallet', 'carte', 'card', 'identifiantwallet'],
    };

    const expectedHeaders = new Set([...(typeHeaders[selectedType] || []), ...commonHeaders]);

    if (expectedHeaders.has(firstCell)) return true;

    return normalizedCells.length > 1 && normalizedCells.every(cell => (
        expectedHeaders.has(cell) || /^[a-z]+$/.test(cell)
    ));
};

const ListImport = () => {
    const { t } = useLanguage();
    const { loadingUserInfos } = useUser();
    const { createCustomersList, loading, error } = useCreateCustomersList();
    const { getCustomersList, loading: loadingLists, data: listsData, invalidateListsCache, isReady, workspaceId } = useGetCustomersList();
    const { getDetailsCustomersList, loading: loadingDetails, data: detailsData } = useGetDetailsCustomersList();
    const { deleteCustomersList, loading: loadingDelete } = useDeleteCustomersList();
    
    const [type, setType] = useState<string>(LIST_TYPES.PERSONID);
    const [importFormResetKey, setImportFormResetKey] = useState(0);
    const [file, setFile] = useState<File | null>(null);
    const [hasHeader, setHasHeader] = useState(false);
    const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerData, setDrawerData] = useState<ImportedList | null>(null);
    const [page, setPage] = useState(INITIAL_PAGE);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const importedLists = useMemo(() => listsData?.member ?? [], [listsData]);
    const totalItems = listsData?.totalItems ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalItems / rowsPerPage));

   
    const tableMinHeight = useMemo(() => {
        const rowHeight = 68; 
        const headerHeight = 56; 
        const paddingHeight = 10; 
        
        if (loadingLists) {
            return headerHeight + (rowsPerPage * rowHeight) + paddingHeight;
        }
        
        const displayedRows = Math.max(1, importedLists.length);
        return headerHeight + (displayedRows * rowHeight) + paddingHeight;
    }, [rowsPerPage, importedLists.length, loadingLists]);

    const getCustomersListRef = useRef(getCustomersList);
    getCustomersListRef.current = getCustomersList;

    // Attendre que isReady soit true (le /me est chargé) avant de fetcher
    useEffect(() => {
        if (!isReady) return;
        getCustomersListRef.current({ page: page + 1, itemsPerPage: rowsPerPage });
    }, [isReady, page, rowsPerPage]);

    // Recharger la liste quand le workspace change
    useWorkspaceChange(() => {
        invalidateListsCache();
        setSelectedRows([]);
        if (page !== INITIAL_PAGE) {
            setPage(INITIAL_PAGE);
        } else {
            getCustomersListRef.current({ page: 1, itemsPerPage: rowsPerPage, forceRefresh: true });
        }
    });

    const resetForm = useCallback(() => {
        setImportFormResetKey(k => k + 1);
        setFile(null);
        setHasHeader(false);
    }, []);

    const handleImport = useCallback(async (label: string, desc: string) => {
        if (!file || !label) {
            toast.warning(t("pages.clients.importList.pleaseSelectFileAndLabel"));
            return;
        }

        if (!hasHeader) {
            let firstLine = '';
            try {
                firstLine = await readFirstNonEmptyLine(file);
            } catch {
                toast.error(t("pages.clients.importList.importError"));
                return;
            }

            if (firstLine && looksLikeHeaderLine(firstLine, type)) {
                toast.error(t("pages.clients.importList.fileHeaderDetected"));
                return;
            }
        }

        const result = await createCustomersList({
            label,
            description: desc,
            fileToImport: file,
            fileContainsHeader: hasHeader,
            isEmailList: type === LIST_TYPES.EMAIL,
            isCellphoneList: type === LIST_TYPES.MOBILE,
            isPersonidList: type === LIST_TYPES.PERSONID,
            isCardList: type === LIST_TYPES.CARD,
            isWalletList: type === LIST_TYPES.WALLET,
        });
        
        if (result && result.success !== false) {
            toast.success(t("pages.clients.importList.importSuccess"));
            resetForm();
            invalidateListsCache();
            if (page !== INITIAL_PAGE) {
                setPage(INITIAL_PAGE); // le useEffect va re-fetcher automatiquement
            } else {
                getCustomersList({ page: 1, itemsPerPage: rowsPerPage, forceRefresh: true });
            }
        } else {
            toast.error(result?.message || error || t("pages.clients.importList.importError"));
        }
    }, [file, hasHeader, type, createCustomersList, error, t, resetForm, getCustomersList, invalidateListsCache, page, rowsPerPage]);

    const handleVisualise = useCallback(async (row: ImportedList) => {
        setDrawerData(row);
        setDrawerOpen(true);
        await getDetailsCustomersList({ 
            list_id: row.LIST_ID,
            ...(row.IS_IDENTIFIER_LIST === '1' && { is_card_list: row.IS_IDENTIFIER_LIST })
        });
    }, [getDetailsCustomersList]);

    const handleRowSelect = useCallback((id: string | number) => {
        setSelectedRows(prev => 
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedRows(prev => 
            prev.length > 0 ? [] : importedLists.map(row => row.LIST_ID)
        );
    }, [importedLists]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
        setPage(newPage);
        setSelectedRows([]);
    }, []);

    const handlePaginationChange = useCallback((_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage - 1);
        setSelectedRows([]);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(INITIAL_PAGE);
        setSelectedRows([]);
    }, []);

    const handleDelete = useCallback(async (list: ImportedList) => {
        setConfirmDialog({
            open: true,
            title: t("common.delete"),
            message: t("pages.clients.importList.confirmDelete"),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                
                try {
                    const result = await deleteCustomersList([convertToDeleteFormat(list)]);
                    
                    if (result) {
                        toast.success(t("pages.clients.importList.deleteSuccess"));
                        invalidateListsCache();
                        if (importedLists.length === 1 && page > 0) {
                            setPage(page - 1);
                        } else {
                            getCustomersList({ page: page + 1, itemsPerPage: rowsPerPage, forceRefresh: true });
                        }
                    }
                } catch (err) {
                    toast.error(t("pages.clients.importList.importError"));
                }
            },
        });
    }, [deleteCustomersList, t, getCustomersList, invalidateListsCache, page, rowsPerPage, importedLists.length]);

    const handleDeleteBatch = useCallback(async () => {
        if (selectedRows.length === 0) return;
        
        setConfirmDialog({
            open: true,
            title: t("pages.clients.importList.deleteBatch"),
            message: t("pages.clients.importList.confirmDeleteBatch").replace("{count}", selectedRows.length.toString()),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                
                try {
                    const listsToDelete = importedLists
                        .filter(list => selectedRows.includes(list.LIST_ID))
                        .map(convertToDeleteFormat);

                    const result = await deleteCustomersList(listsToDelete);
                    
                    if (result) {
                        toast.success(t("pages.clients.importList.deleteBatchSuccess"));
                        setSelectedRows([]);
                        invalidateListsCache();
                        if (selectedRows.length === importedLists.length && page > 0) {
                            setPage(page - 1);
                        } else {
                            getCustomersList({ page: page + 1, itemsPerPage: rowsPerPage, forceRefresh: true });
                        }
                    }
                } catch (err) {
                    toast.error(t("pages.clients.importList.importError"));
                }
            },
        });
    }, [selectedRows, importedLists, deleteCustomersList, t, getCustomersList, invalidateListsCache, page, rowsPerPage]);

    return (
        <PageContainer>
            <PageTitle>
                {t("pages.clients.importList.title")}
            </PageTitle>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <ImportForm
                        key={importFormResetKey}
                        file={file}
                        hasHeader={hasHeader}
                        type={type}
                        loading={loading}
                        onFileChange={setFile}
                        onHasHeaderChange={setHasHeader}
                        onTypeChange={setType}
                        onImport={handleImport}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        {t("pages.clients.importList.importedLists")}
                    </Typography>
                    <TableContainer sx={{ minHeight: `${tableMinHeight}px` }}>
                        <Table>
                            <ListTableHeader
                                selectedCount={selectedRows.length}
                                totalCount={importedLists.length}
                                onSelectAll={handleSelectAll}
                            />
                            <ListTableBody
                                lists={importedLists}
                                loading={loadingLists || loadingUserInfos}
                                selectedRows={selectedRows}
                                onSelectRow={handleRowSelect}
                                onVisualise={handleVisualise}
                                onDelete={handleDelete}
                            />
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="error"
                            type="button"
                            disabled={selectedRows.length === 0 || loadingDelete}
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteBatch();
                            }}
                        >
                            {loadingDelete ? t("pages.clients.importList.deleting") : t("pages.clients.importList.deleteBatch")} {selectedRows.length > 0 && `(${selectedRows.length})`}
                        </Button>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                marginLeft: 'auto',
                            }}
                        >
                            <Pagination
                                count={pageCount}
                                page={Math.min(page + 1, pageCount)}
                                onChange={handlePaginationChange}
                                disabled={loadingLists || loadingUserInfos}
                                siblingCount={1}
                                boundaryCount={1}
                                color="primary"
                                shape="rounded"
                                size="small"
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        fontSize: '0.875rem',
                                        minWidth: 32,
                                        height: 32,
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: '#556ee6!important',
                                        color: '#fff',
                                    },
                                }}
                            />
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={totalItems}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={t("pages.clients.importList.rowsPerPage")}
                                sx={{
                                    '& .MuiTablePagination-toolbar': {
                                        minHeight: 40,
                                        pl: 0,
                                    },
                                    '& .MuiTablePagination-actions': {
                                        ml: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            <DrawerVisualise
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                data={drawerData}
                detailsData={detailsData}
                loadingDetails={loadingDetails}
            />
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                confirmText={t("common.confirm")}
                cancelText={t("common.cancel")}
                confirmColor="error"
            />
        </PageContainer>
    );
};

export default ListImport;
